import { Modal, Box, Typography } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  PanelGroup,
  Panel,
  PanelResizeHandle,
  ImperativePanelGroupHandle,
} from 'react-resizable-panels';
import {
  setAvailableDocumentContent,
  setDocumentContent,
  setDocumentId,
  setDocumentLoading,
  setEcaDocumentViewerModal,
  setExtractedTextPath,
  setHighLightedWords,
  setLeftPanel,
  setNativePath,
  setPanelSize,
  setShowHighLightedTab,
  setTruncatedBannerText,
  setFullScreenView,
} from '../../../redux/slices/ecaDocumentViewerSlice';
import { RootState } from '../../../redux/store';
import AurSvgIcon from '../../common/aur-svg-icon/AurSvgIcon';
import NestedList from './EcaDocumentList';
import EcaDocumentToolBar from './EcaDocumentToolBar';
import AurIcon, { IconColors, IconNames } from '../../common/aur-icon/AurIcon';
import AurIconButton from '../../common/aur-button/AurIconButton';
import { getDocumentSearchText } from '../../../api/EcaManagementService';
import {
  FormattedItem,
  IDocListReqBody,
  IDocListResponse,
  IFilterSet,
} from '../eca-doc-list.util';
import {
  getAllDocResults,
  getSearchResults,
  getSTRDocList,
} from '../../../api/EcaServices';
import { setHighlights } from '../../../redux/slices/ecaDocumentHighLightPanelSlice';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import { AxiosError } from 'axios';
import { useToast } from '../../../contexts/toastContext/ToastContext';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';
import useLRUCache from '../../../hooks/lru-cache/useLRUCache';

interface IViewDocument {
  selectedDocumentIds: string[];
  selectedDocLink: string;
  docsCurrentPage: number;
  docsPerPage: number;
  scrollFetching: boolean;
  searchID?: string;
  filterSet: IFilterSet;
  projectCode?: string | null | undefined;
  reportId?: string;
  selectedTermIds?: string[];
  indexName?: string;
  onDocTextLoad?: (doc: string) => Promise<string>;
  isNlpSearch?: boolean;
  stdIndexFieldTemplateId?: number;
}

interface INestedDocument {
  name: string;
  icon: JSX.Element;
}

// Define the interface for ListItems
interface IDocumentList {
  name: string;
  icon: JSX.Element;
  endIcon?: JSX.Element;
  nestedItems: INestedDocument[];
}

// Document search text model
type DocumentSearchTextModel = {
  truncated?: boolean;
  extractedTextPath?: string;
  nativePath?: string;
  formattedText: string;
}

const ResizableModal = (props: IViewDocument) => {
  const {
    selectedDocumentIds = [],
    selectedDocLink,
    docsCurrentPage,
    docsPerPage,
    scrollFetching,
    searchID,
    indexName,
    filterSet,
    projectCode,
    reportId,
    selectedTermIds,
    onDocTextLoad,
    isNlpSearch = false,
    stdIndexFieldTemplateId,
  } = props;
  const highlightToggleOn = useSelector(
    (state: RootState) => state.ecaHighLightText.toggleOnHighlight
  );

  const highlights = useSelector(
    (state: RootState) => state.ecaHighLightText.highlights
  );

  const documentLoading = useRef(false);
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const pendingPanelSizeRef = useRef<number | null>(null);
  const isPanelDraggingRef = useRef(false);

  const { get: getCachedDocText, set: setCachedDocText } = useLRUCache<string, DocumentSearchTextModel>(5);

  const showHighlights = useSelector(
    (state: RootState) => state.ecaDocumentViewer.showHighLightedTab
  );

  const ecaDocumentViewer = useSelector(
    (state: RootState) => state.ecaDocumentViewer.ecaDocumentViewerPopup
  );

  const availableDocumentContent = useSelector(
    (state: RootState) => state.ecaDocumentViewer.availDocumentContent
  );

  const highLightedWordsData = useSelector(
    (state: RootState) => state.ecaDocumentViewer.highLightedWords
  );

  const showHighLightedTab = useSelector(
    (state: RootState) => state.ecaDocumentViewer.showHighLightedTab
  );
  const strSearchFlag = useSelector(
    (state: RootState) => state.ecaDocumentViewer.strSearchFlag
  );

  const { hasTaggingDocumentPerm } = usePermissionsByPage(
    PAGE_NAME.ECA_DOC_CONTAINER
  );

  const getHighlightsSection = () => {
    const shouldShowHighlights = strSearchFlag
      ? showHighlights
      : showHighLightedTab;

    if (!shouldShowHighlights) return [];

    return [
      {
        name: 'Highlights',
        icon: <AurSvgIcon src="images/eca-highlights-icon.svg" />,
        endIcon: highlightToggleOn ? (
          <AurSvgIcon src="images/eca-doc-highlight-on-icon.svg" />
        ) : (
          <AurSvgIcon src="images/eca-doc-highlight-off-icon.svg" />
        ),
        nestedItems: [
          {
            name: 'Highlights 1',
            icon: <AurSvgIcon src="images/eca-highlights-icon.svg" />,
          },
          {
            name: 'Highlights 2',
            icon: <AurSvgIcon src="images/eca-highlights-icon.svg" />,
          },
        ],
      },
    ];
  };

  const listItems = [
    {
      name: 'Documents',
      icon: <AurSvgIcon src="images/eca-document-icon.svg" />,
      nestedItems: [
        {
          name: 'Documents 1',
          icon: <AurSvgIcon src="images/eca-document-icon.svg" />,
        },
      ],
    },
    ...(hasTaggingDocumentPerm
      ? [
        {
          name: 'Tagging',
          icon: <AurSvgIcon src="images/eca-tagging-icon.svg" />,
          nestedItems: [
            {
              name: 'Tagging 1',
              icon: <AurSvgIcon src="images/eca-tagging-icon.svg" />,
            },
          ],
        },
      ]
      : []),
    ...getHighlightsSection(),
    // ...(showHighLightedTab
    //   ? [
    //     {
    //       name: 'Highlights',
    //       icon: <AurSvgIcon src="images/eca-highlights-icon.svg" />,
    //       endIcon: highlightToggleOn ? (
    //         <AurSvgIcon src="images/eca-doc-highlight-on-icon.svg" />
    //       ) : (
    //         <AurSvgIcon src="images/eca-doc-highlight-off-icon.svg" />
    //       ),

    //       nestedItems: [
    //         {
    //           name: 'Highlights 1',
    //           icon: <AurSvgIcon src="images/eca-highlights-icon.svg" />,
    //         },
    //         {
    //           name: 'Highlights 2',
    //           icon: <AurSvgIcon src="images/eca-highlights-icon.svg" />,
    //         },
    //       ],
    //     },
    //   ]
    //   : []),
    // reverting  ARHD-9605 && ARHD-11249
    // {
    //   name: 'Audit log',
    //   icon: <AurSvgIcon src="images/eca-audit-log-icon.svg" />,
    //   nestedItems: [
    //     {
    //       name: 'Demo 1',
    //       icon: <AurSvgIcon src="images/eca-audit-log-icon.svg" />,
    //     },
    //     {
    //       name: 'Demo 2',
    //       icon: <AurSvgIcon src="images/eca-audit-log-icon.svg" />,
    //     },
    //   ],
    // },
  ];
  const dispatch = useDispatch();

  const handlePanelResize = useCallback(
    (size: number) => {
      pendingPanelSizeRef.current = size;
      if (!isPanelDraggingRef.current) {
        dispatch(setPanelSize(size));
      }
    },
    [dispatch]
  );

  const handlePanelDragging = useCallback(
    (isDragging: boolean) => {
      isPanelDraggingRef.current = isDragging;
      if (!isDragging && pendingPanelSizeRef.current !== null) {
        dispatch(setPanelSize(pendingPanelSizeRef.current));
      }
    },
    [dispatch]
  );

  const [screenLeftResize, setScreenLeftResize] = useState(false);
  const { showToast } = useToast();
  const [fullscreen, setFullScreen] = useState(false);
  const [flexValue, setFlexValue] = useState<string>('');
  const [reqLoading, setReqLoading] = useState<boolean>(false);
  const [listDocuemnts, setListDocuments] =
    useState<IDocumentList[]>(listItems);
  const [activeDocs, setActiveDocs] = useState<string>(
    selectedDocLink ? selectedDocLink : selectedDocumentIds[0]
  );

  const [documentList, setDocumentList] =
    useState<string[]>(selectedDocumentIds);
  const [documentTopCurrentPage, setDocumentTopCurrentPage] =
    useState<number>(docsCurrentPage);
  const [documentBottomCurrentPage, setDocumentBottomCurrentPage] =
    useState<number>(docsCurrentPage);
  const [documentCurrentPageNo, setDocumentCurrentPageNo] =
    useState<number>(docsCurrentPage);
  const [documentPerPage] = useState<number>(docsPerPage);
  const [documentTopLoading, setDocumentTopLoading] = useState<boolean>(false);
  const [documentBottomLoading, setDocumentBottomLoading] =
    useState<boolean>(false);

  const { clientId, projectId } = useSelector(
    (state: RootState) => state.reports
  );

  const resetPanelSizes = () => {
    panelGroupRef.current?.setLayout([30, 70]);
  };

  const handleClick = () => {
    dispatch(setEcaDocumentViewerModal(false));
    dispatch(setHighlights([]));
    dispatch(setShowHighLightedTab(false));
    dispatch(setExtractedTextPath(''));
    dispatch(setNativePath(''));
    dispatch(setTruncatedBannerText(false));
    resetPanelSizes();
  };
  const fullScreenHandler = () => {
    setFullScreen(true);
    dispatch(setFullScreenView(true));
  };
  const minimizeScreenHandler = () => {
    setFullScreen(false);
    dispatch(setFullScreenView(false));
    resetPanelSizes();
  };
  const handleResizeClick = () => {
    setScreenLeftResize(!screenLeftResize);
    if (!screenLeftResize) {
      if (fullScreen) {
        setFlexValue('0 0 56px');
      } else {
        setFlexValue('0 0 56px');
      }
    } else {
      setFlexValue('0 0 440px');
      resetPanelSizes();
    }
  };
  useEffect(() => {
    setFlexValue('23 1 0px');
  }, []);

  // open left panel from list
  const openLeftPanel = () => {
    setScreenLeftResize(false);
    setFlexValue('0 0 440px');
  };

  const addNestedItemsToCategory = (
    categoryName: string,
    nestedItemsArray: string[]
  ) => {
    setListDocuments((prevList: IDocumentList[]) => {
      return prevList.map((item: IDocumentList) => {
        if (item.name === categoryName) {
          // Add the new nested items to the specified category
          item.nestedItems = [
            ...nestedItemsArray.map((name) => ({
              name,
              icon: (
                <AurIcon
                  size="small"
                  icon={IconNames.OtherDocumentIcon}
                  color={IconColors.Custom}
                  customClassName="container-doc-icon"
                />
              ),
            })),
          ];
        }
        return item;
      });
    });
  };

  useEffect(() => {
    setListDocuments((prevList: IDocumentList[]) => {
      return prevList.map((item: IDocumentList) => {
        if (item.name === 'Highlights') {
          item.endIcon = highlightToggleOn ? (
            <AurSvgIcon src="images/eca-doc-highlight-on-icon.svg" />
          ) : (
            <AurSvgIcon src="images/eca-doc-highlight-off-icon.svg" />
          );
        }
        return item;
      });
    });
  }, [highlightToggleOn]);

  const handleClickDocument = (activeDocs: string) => {
    setActiveDocs(activeDocs);
  };

  const handlePreviousPage = () => {
    setActiveDocs(documentList[0]);
  };

  const handleNextPage = () => {
    setActiveDocs(documentList[documentList.length - 1]);
  };

  const handlePrevious = () => {
    const index = documentList.indexOf(activeDocs);
    if (index > 0) {
      const previousDoc = documentList[index - 1];
      setActiveDocs(previousDoc); // Update the activeDocs state
    }
  };

  const handleNext = () => {
    const index = documentList.indexOf(activeDocs);
    if (index <= documentList.length) {
      const previousDoc = documentList[index + 1];
      setActiveDocs(previousDoc); // Update the activeDocs state
    }
  };

  const { columnsList, sortOrder } = useSelector(
    (state: RootState) => state.ecaDocList
  );

  const fullScreen = useSelector(
    (state: RootState) => state.ecaDocumentViewer.fullScreenView
  );

  const title = 'STANDARD';
  const params = `?clientId=${clientId}&projectId=${projectId}`;

  const getDocListResults = async (filters: IFilterSet) => {
    const reqBody: IDocListReqBody = {
      metaDataFields: columnsList.map((col) => col.name),
      sortOrder: sortOrder.map((order) => ({
        column: order.column,
        direction: order.direction,
      })),
      filterSet: filters,
    };
    try {
      const results: IDocListResponse = await getSearchResults(
        params,
        '12345',
        reqBody
      );

      if (results?.data.docList?.length > 0) {
        const formatResponse = results.data.docList.map((item, index) => {
          const formattedItem: FormattedItem = {
            id: String(index + 1),
            documentid: item.docId,
          };
          item.metadata?.forEach((meta) => {
            formattedItem[meta.name] = meta.value;
          });
          return formattedItem;
        });
        const filterData = formatResponse.map(
          (document) => document.documentid
        );
        return filterData;
      } else {
        return [];
      }
    } catch (e) {
      //error handling
    }
  };
  const getAllDocumentResults = async (_documentPerPage: number, _docsCurrentPage: number) => {
    const reqBody: IDocListReqBody = {
      metaDataFields: columnsList.map((col) => col.name),
      sortOrder: sortOrder.map((order) => ({
        column: order.column,
        direction: order.direction,
      })),
      filterSet: filterSet,
    };
    const rowNumber = ((_docsCurrentPage - 1) * _documentPerPage) + 1
    const params = `?clientId=${clientId}&projectId=${projectId}&pageSize=${_documentPerPage}&rowNumber=${rowNumber}&type=Adhoc`;
    try {
      const res = await getAllDocResults(params, stdIndexFieldTemplateId as unknown as string, reqBody);
      return res?.data?.docList.map((item: any) => item.docId);
      // eslint-disable-next-line no-empty
    } catch (e) { }
  };

  const fetchDocuments = async (
    _documentPerPage: number,
    _docsCurrentPage: number
  ) => {
    try {
      if (searchID) {
        return getDocListResults(filterSet);
      }
      return getAllDocumentResults(_documentPerPage, _docsCurrentPage);
    } finally {
      setDocumentBottomLoading(false);
      setDocumentTopLoading(false);
    }
  };

  const getDocumentList = async (
    _documentPerPage: number,
    _docsCurrentPage: number
  ) => {
    try {
      const pagination = {
        searchTerm: '',
        sortColumn: '',
        sortOrder: '',
        pageNumber: _docsCurrentPage,
        pageSize: '100',
      };
      const response = await getSTRDocList(
        projectCode as string,
        reportId as string,
        {
          termIds: selectedTermIds,
          paginationQuery: pagination,
        }
      );
      return response.data.data.documentIds;
    } catch (e) {
      console.error(e);
    } finally {
      setDocumentBottomLoading(false);
    }
  };

  const handleScroll = async (event: React.UIEvent<HTMLElement>) => {
    if (projectCode) {
      const container = event.target as HTMLElement;
      const scrollTolerance = 1;

      if (
        Math.abs(
          container.scrollHeight - container.scrollTop - container.clientHeight
        ) <= scrollTolerance
      ) {
        setDocumentBottomLoading(true);
        const currentPage = documentCurrentPageNo + 1;
        const documentResult = await getDocumentList(
          documentPerPage,
          currentPage
        );
        if (
          documentResult &&
          Array.isArray(documentResult) &&
          documentResult.length > 0
        ) {
          setDocumentList([...documentList, ...documentResult]);
          setDocumentCurrentPageNo(documentCurrentPageNo + 1);
        }
      }
    } else {
      if (scrollFetching) {
        const container = event.target as HTMLElement;
        const atTop = container.scrollTop === 0;
        const scrollTolerance = 1;

        if (
          Math.abs(
            container.scrollHeight -
            container.scrollTop -
            container.clientHeight
          ) <= scrollTolerance
        ) {


          const updatedPageNo = documentBottomCurrentPage + 1;
          try {
            setDocumentBottomLoading(true);
            const documentResult = await fetchDocuments(documentPerPage, updatedPageNo);
            if (documentResult && Array.isArray(documentResult)) {
              setDocumentList((curr) => [...curr, ...documentResult]);
              setDocumentBottomCurrentPage(updatedPageNo);
            } else {
              return;
            }
          } finally {
            setDocumentBottomLoading(false);
          }

        }

        if (atTop) {
          if (documentTopCurrentPage !== 1) {

            const updatedPageNo = documentTopCurrentPage - 1;
            try {
              setDocumentTopLoading(true);
              const documentResult = await fetchDocuments(
                documentPerPage,
                updatedPageNo
              );
              if (documentResult && Array.isArray(documentResult)) {
                setDocumentList((curr) => [...documentResult, ...curr]);
                setDocumentTopCurrentPage(updatedPageNo);
              }
            } finally {
              setDocumentTopLoading(false);
            }
          }
        }
      }
    }
  };

  const fetchDocumentText = async () => {
    if (documentLoading.current === true) return;
    documentLoading.current = true;
    dispatch(setDocumentLoading(true));
    setReqLoading(true);

    try {
      let fetchedText = '';

      let params = `?clientId=${clientId}&projectId=${projectId}`;
      if (!isNlpSearch && searchID) {
        params += `&searchId=${searchID}`;
      } else {
        dispatch(setHighLightedWords([]));
      }
      if (onDocTextLoad) {
        // check if the document text is already cached
        if (getCachedDocText(activeDocs)) {
          fetchedText = getCachedDocText(activeDocs)?.formattedText as string;
        } else {
          fetchedText = await onDocTextLoad(activeDocs);
          setCachedDocText(activeDocs, {
            formattedText: fetchedText,
          });
        }
        dispatch(setDocumentContent(fetchedText));
        dispatch(setAvailableDocumentContent(true));
        return;
      }

      if (getCachedDocText(activeDocs)) {
        const currCachedDoc = getCachedDocText(activeDocs);
        dispatch(setTruncatedBannerText(currCachedDoc?.truncated as boolean));
        dispatch(setExtractedTextPath(currCachedDoc?.extractedTextPath as string));
        dispatch(setNativePath(currCachedDoc?.nativePath as string));
        dispatch(setDocumentContent(currCachedDoc?.formattedText as string));
        dispatch(setAvailableDocumentContent(true));
        return;
      }

      const result = await getDocumentSearchText(activeDocs, params);
      const data = result?.data?.data;
      const responseData = data?.extractedText;

      dispatch(setTruncatedBannerText(data?.truncated));
      dispatch(setExtractedTextPath(data?.extractedTextPath));
      dispatch(setNativePath(data?.nativePath));


      let formattedText = '';

      try {
        const parsedData = JSON.parse(responseData);
        if (Array.isArray(parsedData)) {
          formattedText = parsedData
            .join('')
            .replace(/\r\n/g, '<br>')
            .replace(/\u00A0/g, '&nbsp;');
        } else if (typeof parsedData === 'string') {
          formattedText = parsedData
            .replace(/\r\n/g, '<br>')
            .replace(/\u00A0/g, '&nbsp;');
        } else {
          formattedText = JSON.stringify(parsedData, null, 2)
            .replace(/\r\n/g, '<br>')
            .replace(/\n/g, '<br>')
            .replace(/\u00A0/g, '&nbsp;');
        }
      } catch {
        formattedText = String(responseData)
          .replace(/\r\n/g, '<br>')
          .replace(/\u00A0/g, '&nbsp;');
      }
      // cache the document text
      setCachedDocText(activeDocs, {
        formattedText: formattedText,
        truncated: data?.truncated,
        extractedTextPath: data?.extractedTextPath,
        nativePath: data?.nativePath,
      });
      // set the document content
      dispatch(setDocumentContent(formattedText));
      dispatch(setAvailableDocumentContent(true));
    } catch (error) {
      console.error('Error fetching document text:', error);
      dispatch(setDocumentContent(''));
      dispatch(setAvailableDocumentContent(false));
      const err = error as AxiosError;
      showToast(err.message, 'error', undefined, 'center');
    } finally {
      documentLoading.current = false;
      dispatch(setDocumentLoading(false));
      setReqLoading(false);
    }
  };

  useEffect(() => {
    if (documentList.length != 0) {
      addNestedItemsToCategory('Documents', documentList);
    }
  }, [documentList]);

  useEffect(() => {

    if (documentLoading.current === true) return;

    if (activeDocs && clientId && projectId) {
      fetchDocumentText();
      dispatch(setDocumentId(activeDocs));
    }
  }, [activeDocs, clientId, projectId, searchID]);

  useEffect(() => {
    dispatch(setLeftPanel(screenLeftResize));
  }, [screenLeftResize]);

  const isHighlightsEmpty =
    Array.isArray(highlights) && highlights.length === 0;
  const isHighLightedWordsEmpty =
    Array.isArray(highLightedWordsData) && highLightedWordsData.length === 0;

  return (
    <Modal
      open={ecaDocumentViewer}
      className={
        !fullscreen ? 'eca-document-container' : 'full-screen-view-container'
      }
    >
      <Box
        data-testid="document-viewer"
        className={
          !fullscreen ? 'eca-main-container' : 'eca-main-container-full-view'
        }
        component="aside"
        aria-labelledby="document-preview-title"
      >
        <div className="eca-doc-header">
          <Typography className="typo-text subhead2_size" id="document-preview-title" tabIndex={0}>
            {t('eca.documentPreviewer.documentPreviewTitle')}
          </Typography>

          <div className="eca-doc-header-navigation">
            <AurIconButton
              disabled={documentList.indexOf(activeDocs) === 0}
              onClick={handlePreviousPage}
              className="doc-switch-btn"
              ariaLabel={t('documentNavigation.first')}
              icon={
                <AurSvgIcon
                  src={
                    documentList.indexOf(activeDocs) === 0
                      ? 'images/page_first.svg'
                      : 'images/page_first_filled.svg'
                  }
                />
              }
              dataTestId="Scroll Left"
            />

            <AurIconButton
              disabled={documentList.indexOf(activeDocs) === 0}
              onClick={handlePrevious}
              ariaLabel={t('documentNavigation.previous')}
              className="doc-switch-btn"
              icon={<AurIcon icon={IconNames.ChevronLeft} />}
              dataTestId="Scroll Left"
            />

            <Typography
              className="typo-text subhead3_size document-text"
              tabIndex={0}
            >
              {activeDocs}
            </Typography>

            <AurIconButton
              disabled={
                documentList.indexOf(activeDocs) >= documentList.length - 1
              }
              onClick={handleNext}
              className="doc-switch-btn"
              icon={<AurIcon icon={IconNames.ChevronRight} />}
              dataTestId="Scroll Left"
              ariaLabel={t('documentNavigation.next')}
            />

            <AurIconButton
              disabled={
                documentList.indexOf(activeDocs) >= documentList.length - 1
              }
              onClick={handleNextPage}
              className="doc-switch-btn"
              icon={
                <AurSvgIcon
                  src={
                    documentList.indexOf(activeDocs) >= documentList.length - 1
                      ? 'images/page_last.svg'
                      : 'images/page_last_filled.svg'
                  }
                />
              }
              dataTestId="Scroll Left"
              ariaLabel={t('documentNavigation.last')}
            />
          </div>

          <div className="eca-doc-header-navigation">
            <AurIconButton
              onClick={!fullscreen ? fullScreenHandler : minimizeScreenHandler}
              className="doc-switch-btn"
              icon={
                <AurSvgIcon
                  src={
                    !fullScreen
                      ? 'images/eca-fullscreen-icon.svg'
                      : 'images/eca-doc-minimize-icon.svg'
                  }
                />
              }
              dataTestId="fullscreen button"
              ariaLabel={
                !fullScreen
                  ? t('documentNavigation.expand')
                  : t('documentNavigation.collapse')
              }
            />

            <AurIconButton
              onClick={handleClick}
              icon={<AurSvgIcon src="images/eca-close-docs-icon.svg" />}
              className="doc-switch-btn"
              dataTestId="close button"
              ariaLabel={t('documentNavigation.close')}
            />
          </div>
        </div>

        <PanelGroup id="eca-document-viewer-panel-group" direction="horizontal" ref={panelGroupRef}>
          <Panel
            id="eca-document-list-panel"
            order={1}
            style={{ flex: flexValue, overflow: 'hidden' }}
            minSize={31}
            maxSize={90}
            onResize={handlePanelResize}
          >
            <Box
              id="eca-document-list-panel-content"
              className={
                !screenLeftResize
                  ? 'eca-main-container-show-eca-drawer-document'
                  : 'eca-main-document-container'
              }
            >
              <button
                type="button"
                onClick={() => handleResizeClick()}
                style={{ cursor: 'pointer', borderBottom: '1px solid #2D3D55' }}
                className="resize-button"
                aria-label={t('documentNavigation.resizePanel')}
                aria-expanded={!screenLeftResize}
                aria-controls='eca-document-list-panel-content'
                tabIndex={0}
              >
                <AurSvgIcon src="images/eca-doc-resize-icon.svg" />
              </button>

              <NestedList
                items={listDocuemnts}
                activeDocs={activeDocs}
                handleClickDocument={handleClickDocument}
                handleScroll={handleScroll}
                documentTopLoading={documentTopLoading}
                documentBottomLoading={documentBottomLoading}
                searchId={Number(props.searchID)}
                indexName={indexName ?? ''}
                stdIndexFieldTemplateId={stdIndexFieldTemplateId}
                openLeftPanel={openLeftPanel}
              />
            </Box>
          </Panel>

          <PanelResizeHandle
            id="eca-document-preview-resize-handle"
            className="cursor-ew-resize"
            aria-label={t('documentNavigation.resizePanel')}
            tabIndex={-1}
            onDragging={handlePanelDragging}
          />
          <Panel
            id="eca-document-preview-panel"
            order={2}
          >
            <Box
              className={
                !screenLeftResize
                  ? 'eca-main-container-eca-drawer-document-default-view'
                  : 'eca-main-document-container-previewer'
              }
            >
              {availableDocumentContent ? <EcaDocumentToolBar /> : null}
            </Box>
          </Panel>
          {isHighLightedWordsEmpty ? (
            <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
          ) : null}

          {strSearchFlag && isHighlightsEmpty ? (
            <AurLoadingIndicator overlay={true} isOpen={reqLoading} />
          ) : null}
        </PanelGroup>
      </Box>
    </Modal>
  );
};

export default ResizableModal;
